import styled from '@emotion/styled'
import { useEffect, useRef } from 'react'
import SVG from '../assets/svg'
import { Button, CircularProgress } from '@mui/material'
import { variants } from '../styles/animation'
import { AnimatePresence, motion } from 'framer-motion'
import { color, screen, size, space } from '../styles'
import { maxWidth, padding } from '../styles/size'

interface props {
   visible: boolean
   type?: 'error' | 'warning' | 'default' | 'success'
   title?: string
   description?: string | React.ReactNode
   canIgnore?: boolean
   actionButtons?: {
      accept?: {
         label: string
         onClick?: () => void
      }
      cancel?: {
         label: string
         onClick?: () => void
      }
   }
   onClose: () => any
   renderComponent?: {
      show: boolean
      component: () => JSX.Element | null
   }
   loading?: boolean
   className?: string
   fullscreenOnMobile?: boolean
   alertMessage?: string
}

const Modal: React.FC<props> = (props) => {
   const { type, title, description, canIgnore, actionButtons, visible, renderComponent, loading } =
      props

   const dialogRef = useRef<HTMLDialogElement>(null)

   const close = () => {
      if (!!props.alertMessage) {
         const result = window.confirm(props.alertMessage)
         if (result) {
            props.onClose()
         }
      } else {
         props.onClose()
      }
   }

   // hide modal when pressing on ESC key, and disable it's default behavior
   useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
         if (e.key === 'Escape') {
            e.preventDefault()
            visible && close()
         }
      }
      document.addEventListener('keydown', handleKeyDown)
      return () => {
         document.removeEventListener('keydown', handleKeyDown)
      }
   }, [visible])

   useEffect(() => {
      visible && dialogRef.current?.showModal()
   }, [visible])

   const handleAccept = () => {
      actionButtons?.accept?.onClick && actionButtons?.accept?.onClick()
      loading === undefined && close()
   }
   const handleCancel = () => {
      actionButtons?.cancel?.onClick && actionButtons?.cancel?.onClick()
      close()
   }
   const handleIgnore = () => {
      close()
   }

   return (
      <AnimatePresence>
         {visible && (
            <Holder
               ref={dialogRef}
               animate="reveal"
               initial="disappearDown"
               variants={variants}
               exit="disappearDown"
               className={
                  (props.className || '') + (props.fullscreenOnMobile ? ' full-screen' : '')
               }
               key={type}
            >
               {renderComponent?.show && (
                  <div className="render-container">
                     {canIgnore && (
                        <SVG.form.close onClick={handleIgnore} className="ignore-button" />
                     )}
                     <div className="render-component">{renderComponent.component()}</div>
                  </div>
               )}
               {!renderComponent?.show && (
                  <div className={'modal ' + type || 'default'} data-testid="modal">
                     {type === 'error' && <SVG.modal.error className="illustration" />}
                     {type === 'warning' && <SVG.modal.warning className="illustration" />}
                     {type === 'success' && <SVG.modal.success className="illustration" />}
                     {(canIgnore || !actionButtons) && (
                        <SVG.form.close onClick={handleIgnore} className="ignore-button" />
                     )}
                     {title && <h3 className="title">{title}</h3>}
                     {type !== 'default' && <SVG.modal.background className="background-curve" />}
                     <p className="main-description">{description}</p>
                     {actionButtons && (
                        <div className="action-buttons">
                           {actionButtons.cancel && (
                              <Button
                                 className="cancel"
                                 onClick={handleCancel}
                                 variant="outlined"
                                 color="secondary"
                              >
                                 {actionButtons.cancel.label}
                              </Button>
                           )}
                           {actionButtons.accept && (
                              <Button
                                 className="accept"
                                 onClick={handleAccept}
                                 variant="contained"
                                 color="primary"
                                 disabled={loading}
                                 endIcon={loading ? <CircularProgress size={'100%'} /> : null}
                              >
                                 {actionButtons.accept.label}
                              </Button>
                           )}
                        </div>
                     )}
                  </div>
               )}
            </Holder>
         )}
      </AnimatePresence>
   )
}

const Holder = styled(motion.dialog)`
   display: flex;
   flex-direction: column;
   gap: ${space.s};
   border-radius: 0.5em;
   overflow: hidden;
   overflow-y: auto;
   box-shadow: 0 0 0.5em rgba(0, 0, 0, 0.2);
   padding: ${padding.block} ${padding.inline};
   border: none !important;
   outline: none !important;
   max-width: ${maxWidth.mainSection};
   box-sizing: border-box;
   width: 100%;

   &::backdrop {
      background: rgba(0, 0, 0, 0.1);
      backdrop-filter: blur(7px);
   }

   .modal,
   .render-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      box-sizing: border-box;
      .ignore-button {
         position: absolute;
         top: 1.5em;
         right: 1.5em;
         cursor: pointer;
         opacity: 0.5;
         transition: opacity 0.2s;
         fill: ${color.grayDark};
         width: ${size.l};
         aspect-ratio: 1;
         z-index: 4;
         &:hover {
            opacity: 0.9;
         }
      }

      .title {
         max-width: 18em;
         white-space: nowrap;
         overflow: hidden;
         text-overflow: ellipsis;
      }
   }

   .modal {
      min-width: 15em;
      --active-color: ${color.green};

      .illustration {
         position: relative;
         width: 6em;
         margin-block: ${space.xs};
      }

      .main-description {
         text-align: center;

         p,
         span,
         b {
            color: inherit;
         }
      }

      .action-buttons {
         display: flex;
         gap: ${padding.inline};
         align-items: center;
         justify-content: center;
      }

      .background-curve {
         position: absolute;
         top: 0;
         left: 0;
         width: 100%;
         fill: var(--active-color);
      }

      &.error {
         --active-color: ${color.red};
      }
      &.warning {
         --active-color: ${color.yellow};
      }
      &.success {
         --active-color: ${color.green};
      }
   }

   .render-container {
      background-color: ${color.white};
      .render-component {
         display: flex;
         flex-direction: column;
         align-items: center;
         justify-content: center;
      }

      /* Modal Buttons  */
      .modal-action-buttons {
         display: flex;
         flex-direction: row;
         align-items: center;
         justify-content: center;
         gap: ${padding.inline};

         button {
            width: 100%;
         }
         button[type='submit'] {
            padding: 0.6em 1.5em !important;
         }
      }
   }

   ${screen.onlyMobile} {
      &.full-screen {
         margin-block: 0;
         width: calc(100% - ${padding.inline} * 2);
         height: 100dvh;
         box-sizing: content-box;
         border-radius: 0;
         justify-content: center;
      }
   }
`

export type { props as ModalProps }
export default Modal
